import { useCallback, useMemo, useState } from "react";
import type { Dp, Filter } from "./ExState";
import { makeCol, StatsTable } from "../components/StatsTable2";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import { IconButton } from "@mui/material";
import OpenInFull from "@mui/icons-material/OpenInFull";
import { make as UnitImageExtended } from "common/src/tft-static/UnitImageExtended.bs";
import { make as UnitName0 } from "common/src/tft-static/UnitName0.bs";
import { use as useSet } from "common/src/data/TftSet.bs";
import { searchUnits } from "../common/Search2";
import { make as Tooltip } from "common/src/components/Tooltip.bs";

const cols = [
  makeCol({
    width: 180,
    order: ([unitId], _, { unitsT }) => unitsT(unitId),
    defaultDesc: false,
    renderTitle: (_, { t }) => <> {t("Unit")} </>,
    renderValue: ([unitId, starLevel, _]) => (
      <div className="flex items-center text-sm">
        <div className="flex-shrink-0">
          <UnitImageExtended
            unitId={unitId}
            size={28}
            items={[]}
            starLevel={starLevel}
            forceStarLevel
            showName={false}
          />
        </div>
        <div className="pl-[6px] font-roboto font-normal truncate">
          <UnitName0 unit={unitId} />
        </div>
      </div>
    ),
  }),
  makeCol({
    width: 90,
    order: ([_, __, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Games")} </>,
    renderValue: ([_, __, v]) => <> {humanReadableCount(v.count)} </>,
  }),
  makeCol({
    width: 90,
    order: ([_, __, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Play rate")} </>,
    tooltip: ([_, __, v]) => <> {humanReadableCount(v.count) + " games"} </>,
    renderValue: ([_, __, v], { baseCount }) => (
      <> {formatPct2(v.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: ([_, __, v], { hidePlacements }) =>
      hidePlacements ? 0 : v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Place")} </>,
    renderValue: ([_, __, v], { hidePlacements }) => (
      <> {hidePlacements ? "—" : (v.place / v.count).toFixed(2)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: ([_, __, v], { hidePlacements }) => (hidePlacements ? 0 : v.delta),
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => (
      <Tooltip
        title={
          "Delta shows the difference in average placement of the selected filter combination with the unit compared to the average placement of the filter combination without the unit. Lower is better."
        }
      >
        <div>{t("Delta")}</div>
      </Tooltip>
    ),
    renderValue: ([_, __, v], { hidePlacements }) => (
      <>
        {" "}
        {hidePlacements
          ? "—"
          : (v.delta > 0 ? "+" : "") + v.delta.toFixed(2)}{" "}
      </>
    ),
  }),
  makeCol({
    width: 90,
    order: ([_, __, v], { hidePlacements }) =>
      hidePlacements ? 0 : v.top4 / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Top 4")}% </>,
    renderValue: ([_, __, v], { hidePlacements }) => (
      <> {hidePlacements ? "—" : formatPct2(v.top4 / v.count)} </>
    ),
  }),
  makeCol({
    width: 80,
    order: ([_, __, v], { hidePlacements }) =>
      hidePlacements ? 0 : v.won / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Win")}% </>,
    renderValue: ([_, __, v], { hidePlacements }) => (
      <> {hidePlacements ? "—" : formatPct2(v.won / v.count)} </>
    ),
  }),
  makeCol({
    width: 48,
    alignRight: true,
    renderTitle: (_) => <div className="h-1" />,
    valueClass: "!justify-start",
    renderValue: (
      [unitId, starLevel],
      { onExpand, hideExpand },
      { unitsT },
    ) => {
      return hideExpand ? (
        <div />
      ) : (
        <>
          <IconButton
            onClick={(_) =>
              onExpand({
                typ: "u",
                id: unitId,
                sid: unitId,
                starLevel: starLevel,
                label: unitsT(unitId),
              })
            }
            classes={{
              root: "text-lg",
            }}
          >
            <OpenInFull fontSize="inherit" />
          </IconButton>
        </>
      );
    },
  }),
];

type Props = {
  data: Array<[string, number, Dp]>;
  baseCount: number;
  onExpand: (f: Filter) => void;
  search: string;
  hideExpand: boolean;
  minSample: number;
  hidePlacements: boolean;
};

const ExStarUnits = ({
  data,
  baseCount,
  onExpand,
  search,
  hideExpand,
  minSample,
  hidePlacements,
}: Props) => {
  const set = useSet();
  const keyFn = useCallback(
    ([id, starLevel, _]: [string, number, Dp]) => id + starLevel,
    [],
  );
  const context = useMemo(
    () => ({ baseCount, onExpand, hideExpand, hidePlacements }),
    [baseCount, onExpand, hideExpand, hidePlacements],
  );

  const filteredData = useMemo(
    () => data.filter(([_, _1, v]) => v.count >= minSample),
    [data, minSample],
  );

  const visibleFilter = useMemo(() => {
    if (search === "") {
      return () => true;
    }
    const passIds = new Set(searchUnits(search, set));
    return ([unitId]: [string]) => passIds.has(unitId);
  }, [search]);

  if (data.length === 0) {
    return (
      <div className="px-5 py-6 text-xl text-medium text-white2">
        No unit combos with at least 100 games played
      </div>
    );
  } else {
    return (
      <StatsTable
        cols={cols}
        context={context}
        data={filteredData}
        keyFn={keyFn}
        stickyColCount={1}
        defaultSortIdx={1}
        top="t47m92"
        visibleFilter={visibleFilter}
      />
    );
  }
};

export default ExStarUnits;
